import { Link } from "react-router-dom";
import { useState } from "react";
import "./index.css";

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  // Contact form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: "",
  });

  // Redesigned features focusing on entertainment and IPTV
  const features = [
    {
      title: "Ultra HD Streaming",
      desc: "Immerse yourself in crystal-clear HDR quality with Dolby Atmos sound for the ultimate viewing experience.",
      icon: "🎬",
    },
    {
      title: "Multi-Device Sync",
      desc: "Start watching on your TV, continue on your phone. Your watchlist and progress sync seamlessly across all devices.",
      icon: "📱",
    },
    {
      title: "Global Content Library",
      desc: "Access thousands of movies, TV shows, and live channels from around the world in multiple languages.",
      icon: "🌍",
    },
    {
      title: "Smart Recommendations",
      desc: "Our AI-powered engine learns your preferences and suggests content you'll love, making discovery effortless.",
      icon: "🤖",
    },
    {
      title: "Offline Viewing",
      desc: "Download your favorite shows and movies to watch offline, perfect for travel or areas with limited connectivity.",
      icon: "⬇️",
    },
    {
      title: "Parental Controls",
      desc: "Keep your family safe with advanced parental controls, kid-friendly profiles, and content filtering options.",
      icon: "👨‍👩‍👧‍👦",
    },
  ];

  // Redesigned subscription plans
  const plans = [
    {
      name: "Starter",
      price: "₹149",
      period: "/month",
      features: ["100+ Live TV Channels", "HD Quality", "1 Device", "Basic Support"],
      popular: false,
      color: "bg-blue-500",
    },
    {
      name: "Pro",
      price: "₹299",
      period: "/month",
      features: ["250+ Live TV Channels", "Full HD Quality", "3 Devices", "Premium Support", "Cloud DVR"],
      popular: true,
      color: "bg-purple-600",
    },
    {
      name: "Ultimate",
      price: "₹499",
      period: "/month",
      features: ["400+ Live TV Channels", "HDR Quality", "Unlimited Devices", "24/7 Priority Support", "Cloud DVR 100hrs", "Exclusive Content"],
      popular: false,
      color: "bg-gradient-to-r from-amber-500 to-orange-500",
    },
  ];

  const faqs = [
    {
      q: "What is Master Play?",
      a: "Master Play offers a premium entertainment platform SITIPLAY, that brings together live TV channels, movies, and exclusive content from around the world in one seamless experience.",
    },
    {
      q: "Can I cancel anytime?",
      a: "No, you can't cancel your subscription at any time without any penalties. Your access will continue until the end of your billing cycle.",
    },
    {
      q: "What internet speed do I need?",
      a: "We recommend a minimum of 25 Mbps for HD streaming. However, our adaptive streaming technology works well on lower speeds too.",
    },
  ];

  // Redesigned device list
  const devices = [
    { name: "Android TV", img: "https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?w=400&q=80", available: true },
    { name: "Google", img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&q=80", available: true },
    { name: "Fire TV Stick", img: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400&q=80", available: true },
    { name: "Samsung Smart TV", img: "https://images.unsplash.com/photo-1461151304267-38535e780c79?w=400&q=80", available: false },
    { name: "LG webOS", img: "https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?w=400&q=80", available: false },
  ];

  // Testimonials
  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Premium User",
      text: "Master Play has completely transformed my entertainment experience. The HDR quality and vast content library are unmatched!",
      rating: 5,
    },
    {
      name: "Priya",
      role: "Family Plan User",
      text: "My whole family loves Master Play. The parental controls give me peace of mind while my kids enjoy their favorite shows.",
      rating: 5,
    },
    {
      name: "Ravi Kiran",
      role: "Tech Enthusiast",
      text: "The seamless multi-device sync and offline viewing features make Master Play the best streaming platform I've ever used.",
      rating: 5,
    },
  ];

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: false,
        error: true,
        message: "Please fill in all required fields.",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        submitted: false,
        error: true,
        message: "Please enter a valid email address.",
      });
      return;
    }

    setTimeout(() => {
      setFormStatus({
        submitted: true,
        error: false,
        message: "Thank you for contacting us! Our team will get back to you within 24 hours.",
      });
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    }, 1000);
  };

  return (
    <div className="sitiplay-home">

      {/* ── NAVBAR ───────────────────────────────────────── */}
      <header className={`sitiplay-navbar ${isMenuOpen ? "sitiplay-menu-open" : ""}`}>
        <div className="sitiplay-navbar-container">
          <a href="#hero" className="sitiplay-logo-wrapper">
            <img src="https://masterplay.in/images/masterplay.png" alt="Master Play Logo" className="sitiplay-logo-img" />
            {/* <span className="sitiplay-logo-text">SITIPLAY</span> */}
          </a>

          <nav className="sitiplay-nav-links">
            <a href="#features" className="sitiplay-nav-link">Features</a>
            <a href="#plans" className="sitiplay-nav-link">Plans</a>
            <a href="#devices" className="sitiplay-nav-link">Devices</a>
            <a href="#faq" className="sitiplay-nav-link">FAQ</a>
            <a href="#contact" className="sitiplay-nav-link">Contact</a>
          </nav>

          <div className="sitiplay-desktop-cta">
            {/* <Link to="/login" className="sitiplay-login-btn">Sign In</Link> */}
            <Link to="/login" className="sitiplay-signup-btn">Login</Link>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="sitiplay-mobile-menu-btn"
            aria-label="Toggle menu"
          >
            <span className={`sitiplay-hamburger ${isMenuOpen ? "sitiplay-active" : ""}`}>
              <span></span><span></span><span></span>
            </span>
          </button>
        </div>

        <div className={`sitiplay-mobile-menu ${isMenuOpen ? "sitiplay-open" : ""}`}>
          <a href="#features" className="sitiplay-mobile-nav-link" onClick={() => setIsMenuOpen(false)}>Features</a>
          <a href="#plans" className="sitiplay-mobile-nav-link" onClick={() => setIsMenuOpen(false)}>Plans</a>
          <a href="#devices" className="sitiplay-mobile-nav-link" onClick={() => setIsMenuOpen(false)}>Devices</a>
          <a href="#faq" className="sitiplay-mobile-nav-link" onClick={() => setIsMenuOpen(false)}>FAQ</a>
          <a href="#contact" className="sitiplay-mobile-nav-link" onClick={() => setIsMenuOpen(false)}>Contact</a>
          <Link to="/login" className="sitiplay-mobile-login-btn" onClick={() => setIsMenuOpen(false)}>
            Sign In
          </Link>
          <Link to="/login" className="sitiplay-mobile-signup-btn" onClick={() => setIsMenuOpen(false)}>
            Login
          </Link>
        </div>
      </header>

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="sitiplay-hero" id="hero">
        <div className="sitiplay-hero-bg">
          <div className="sitiplay-hero-bg-gradient"></div>
          <div className="sitiplay-hero-particles"></div>
        </div>
        <div className="sitiplay-hero-bg">
          <img
            src="https://sitiplay.com/images/tv.png"
            alt="Cinema background"
            className="sitiplay-hero-bg-img"
          />
          <div className="sitiplay-hero-bg-gradient"></div>
        </div>

        <div className="sitiplay-hero-content">
          <div className="sitiplay-hero-inner">

            <h1 className="sitiplay-hero-title">
              Entertainment 
              <span className="sitiplay-text-gradient"> Without Limits</span>
            </h1>

            <p className="sitiplay-hero-description">
              Master Play, powered by Master Channel Community Network Pvt. Ltd. (MCCN), is India's next-generation entertainment platform bringing you Live TV, Movies, Sports, Devotional content, Kids entertainment, and much more in stunning HD quality. Stream anytime, anywhere, on every screen.
            </p>

            <div className="sitiplay-hero-buttons">
              <Link to="/login" className="sitiplay-btn-primary">
                Login <span className="sitiplay-btn-arrow">→</span>
              </Link>
              <a href="#plans" className="sitiplay-btn-secondary">View Plans</a>
            </div>

            <div className="sitiplay-hero-stats">
              <div className="sitiplay-stat-item">
                <div className="sitiplay-stat-value">50K+</div>
                <div className="sitiplay-stat-label">Happy Users</div>
              </div>
              <div className="sitiplay-stat-divider"></div>
              <div className="sitiplay-stat-item">
                <div className="sitiplay-stat-value">10K+</div>
                <div className="sitiplay-stat-label">Content Titles</div>
              </div>
              <div className="sitiplay-stat-divider"></div>
              <div className="sitiplay-stat-item">
                <div className="sitiplay-stat-value">4.9★</div>
                <div className="sitiplay-stat-label">User Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────── */}
      <section id="features" className="sitiplay-features-section">
        <div className="sitiplay-container">
          <div className="sitiplay-section-header">
            <span className="sitiplay-section-eyebrow">Why Choose Master Play</span>
            <h2 className="sitiplay-section-title">Premium Features for <span className="sitiplay-text-gradient">Ultimate Entertainment</span></h2>
            <p className="sitiplay-section-subtitle">Everything you need for an exceptional streaming experience</p>
          </div>

          <div className="sitiplay-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="sitiplay-feature-card">
                <div className="sitiplay-feature-icon">{feature.icon}</div>
                <h3 className="sitiplay-feature-title">{feature.title}</h3>
                <p className="sitiplay-feature-desc">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLANS ────────────────────────────────────────── */}
      <section id="plans" className="sitiplay-plans-section">
        <div className="sitiplay-container">
          <div className="sitiplay-section-header">
            <span className="sitiplay-section-eyebrow">Pricing Plans</span>
            <h2 className="sitiplay-section-title">Choose Your <span className="sitiplay-text-gradient">Perfect Plan</span></h2>
            <p className="sitiplay-section-subtitle">Flexible options for every entertainment need</p>
          </div>

          <div className="sitiplay-plans-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`sitiplay-plan-card ${plan.popular ? "sitiplay-popular" : ""}`}>
                {plan.popular && <div className="sitiplay-plan-badge">⭐ Most Popular</div>}
                <h3 className="sitiplay-plan-name">{plan.name}</h3>
                <div className="sitiplay-plan-price">
                  <span className="sitiplay-plan-price-value">{plan.price}</span>
                  <span className="sitiplay-plan-price-period">{plan.period}</span>
                </div>
                <ul className="sitiplay-plan-features">
                  {plan.features.map((f, i) => (
                    <li key={i} className="sitiplay-plan-feature-item">
                      <span className="sitiplay-plan-feature-icon">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button className={`sitiplay-plan-btn ${plan.popular ? "sitiplay-btn-gradient" : "sitiplay-btn-outline"}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEVICES ──────────────────────────────────────── */}
      <section id="devices" className="sitiplay-devices-section">
        <div className="sitiplay-container">
          <div className="sitiplay-section-header">
            <span className="sitiplay-section-eyebrow">Compatibility</span>
            <h2 className="sitiplay-section-title">Watch on <span className="sitiplay-text-gradient">Any Device</span></h2>
            <p className="sitiplay-section-subtitle">Seamless streaming across all your favorite devices</p>
          </div>

          <div className="sitiplay-devices-grid">
            {devices.map((d, i) => (
              <div key={i} className={`sitiplay-device-card ${!d.available ? "sitiplay-device-coming" : ""}`}>
                <div className="sitiplay-device-img-wrap">
                  <img src={d.img} alt={d.name} className="sitiplay-device-img" />
                  {!d.available && (
                    <div className="sitiplay-coming-soon-badge">Coming Soon</div>
                  )}
                </div>
                <div className="sitiplay-device-info">
                  <span className="sitiplay-device-name">{d.name}</span>
                  <span className={`sitiplay-device-status ${d.available ? "sitiplay-available" : "sitiplay-coming"}`}>
                    {d.available ? "✓ Available" : "🚀 Coming Soon"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────── */}
      <section id="testimonials" className="sitiplay-testimonials-section">
        <div className="sitiplay-container">
          <div className="sitiplay-section-header">
            <span className="sitiplay-section-eyebrow">Testimonials</span>
            <h2 className="sitiplay-section-title">What Our <span className="sitiplay-text-gradient">Users Say</span></h2>
            <p className="sitiplay-section-subtitle">Real stories from real people who love Master Play</p>
          </div>

          <div className="sitiplay-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="sitiplay-testimonial-card">
                <div className="sitiplay-testimonial-rating">
                  {"★".repeat(testimonial.rating)}
                  {"☆".repeat(5 - testimonial.rating)}
                </div>
                <p className="sitiplay-testimonial-text">"{testimonial.text}"</p>
                <div className="sitiplay-testimonial-author">
                  <div className="sitiplay-testimonial-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="sitiplay-testimonial-name">{testimonial.name}</div>
                    <div className="sitiplay-testimonial-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section id="faq" className="sitiplay-faq-section">
        <div className="sitiplay-container">
          <div className="sitiplay-section-header">
            <span className="sitiplay-section-eyebrow">Got Questions?</span>
            <h2 className="sitiplay-section-title">Frequently Asked <span className="sitiplay-text-gradient">Questions</span></h2>
            <p className="sitiplay-section-subtitle">Everything you need to know about Master Play</p>
          </div>

          <div className="sitiplay-faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className={`sitiplay-faq-item ${activeFaq === index ? "sitiplay-active" : ""}`}>
                <button onClick={() => toggleFaq(index)} className="sitiplay-faq-question">
                  <span className="sitiplay-faq-question-text">{faq.q}</span>
                  <span className={`sitiplay-faq-arrow ${activeFaq === index ? "sitiplay-rotated" : ""}`}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>
                <div className="sitiplay-faq-answer">
                  <div className="sitiplay-faq-answer-content">
                    <p>{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────────────────── */}
      <section className="sitiplay-cta-banner">
        <div className="sitiplay-container">
          <div className="sitiplay-cta-wrapper">
            <div className="sitiplay-cta-content">
              <h2 className="sitiplay-cta-title">
                Ready to <span className="sitiplay-text-gradient">Unleash</span> Your Entertainment?
              </h2>
              <p className="sitiplay-cta-description">
                Join thousands of happy users and start today. 
                No commitment.
              </p>
              <Link to="/login" className="sitiplay-cta-btn">
                Start <span className="sitiplay-btn-arrow">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT US ─────────────────────────────────── */}
      <section id="contact" className="sitiplay-contact-section">
        <div className="sitiplay-container">
          <div className="sitiplay-section-header">
            <span className="sitiplay-section-eyebrow">Get In Touch</span>
            <h2 className="sitiplay-section-title">We're Here to <span className="sitiplay-text-gradient">Help</span></h2>
            <p className="sitiplay-section-subtitle">
              Have questions or need support? Reach out to us and we'll get back to you promptly.
            </p>
          </div>

          <div className="sitiplay-contact-grid">
            <div className="sitiplay-contact-info">
              <div className="sitiplay-contact-info-card">
                <div className="sitiplay-contact-icon-wrapper">
                  <span className="sitiplay-contact-icon">📧</span>
                </div>
                <div>
                  <h3 className="sitiplay-contact-info-title">Email</h3>
                  <p className="sitiplay-contact-info-text">support@masterplay.in</p>
                </div>
              </div>

              <div className="sitiplay-contact-info-card">
                <div className="sitiplay-contact-icon-wrapper">
                  <span className="sitiplay-contact-icon">📞</span>
                </div>
                <div>
                  <h3 className="sitiplay-contact-info-title">Phone</h3>
                  <p className="sitiplay-contact-info-text">+91 888 607 84 84</p>
                  <p className="sitiplay-contact-info-subtext">Mon-Sat, 10AM - 6PM IST</p>
                </div>
              </div>

              <div className="sitiplay-contact-info-card">
                <div className="sitiplay-contact-icon-wrapper">
                  <span className="sitiplay-contact-icon">📍</span>
                </div>
                <div className="sitiplay-contact-details">
                  <h3 className="sitiplay-contact-info-title">Visit Us</h3>
                  <p className="sitiplay-contact-info-text">
                    <strong>Master Channel Community Network PVT LTD</strong><br />
                    Vijayawada Andhra Pradesh India 520010
                  </p>
                </div>
              </div>

              <div className="vrplay-contact-social">
                <h3 className="vrplay-contact-info-title">Follow Us</h3>
                <div className="vrplay-social-links">
                  <a href="#" className="vrplay-social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                    <svg className="vrplay-social-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  
                  <a href="#" className="vrplay-social-link" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                    <svg className="vrplay-social-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  
                  <a href="#" className="vrplay-social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <svg className="vrplay-social-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                  </a>
                  
                  <a href="#" className="vrplay-social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <svg className="vrplay-social-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  
                  <a href="#" className="vrplay-social-link" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
                    <svg className="vrplay-social-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>


            <div className="sitiplay-contact-form-wrapper">
              {formStatus.submitted ? (
                <div className="sitiplay-form-success">
                  <div className="sitiplay-success-icon">✅</div>
                  <h3 className="sitiplay-success-title">Message Sent!</h3>
                  <p className="sitiplay-success-message">{formStatus.message}</p>
                  <button
                    onClick={() => setFormStatus({ submitted: false, error: false, message: "" })}
                    className="sitiplay-btn-primary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="sitiplay-contact-form">
                  <div className="sitiplay-form-row">
                    <div className="sitiplay-form-group">
                      <label htmlFor="name" className="sitiplay-form-label">
                        Full Name <span className="sitiplay-required">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className="sitiplay-form-input"
                        required
                      />
                    </div>
                    <div className="sitiplay-form-group">
                      <label htmlFor="email" className="sitiplay-form-label">
                        Email Address <span className="sitiplay-required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        className="sitiplay-form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="sitiplay-form-group">
                    <label htmlFor="subject" className="sitiplay-form-label">
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="sitiplay-form-input sitiplay-form-select"
                    >
                      <option value="">Select a topic</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="feedback">Feedback</option>
                      <option value="feature">Feature Request</option>
                    </select>
                  </div>

                  <div className="sitiplay-form-group">
                    <label htmlFor="message" className="sitiplay-form-label">
                      Message <span className="sitiplay-required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="How can we help you?"
                      className="sitiplay-form-input sitiplay-form-textarea"
                      rows="5"
                      required
                    />
                  </div>

                  {formStatus.error && (
                    <div className="sitiplay-form-error">
                      <span className="sitiplay-error-icon">⚠️</span>
                      {formStatus.message}
                    </div>
                  )}

                  <button type="submit" className="sitiplay-btn-primary sitiplay-submit-btn">
                    Send Message <span className="sitiplay-btn-arrow">→</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────── */}
      <footer className="sitiplay-footer">
        <div className="sitiplay-footer-container">
          <div className="sitiplay-footer-grid">
            <div className="sitiplay-footer-brand">
              <a href="#hero" className="sitiplay-footer-logo">
                <img src="https://masterplay.in/images/masterplay.png" alt="Master Play" className="sitiplay-footer-logo-img" />
                {/* <span className="sitiplay-footer-logo-text">Master Play</span> */}
              </a>
              <p className="sitiplay-footer-tagline">A premium entertainment service by Master Channel Community Network PVT LTD.</p>
              <p className="sitiplay-footer-tagline2">#32-2-5 , RATNA COMPLEX AJAMMA STREET,PRAJASAKTHI Moghalrajpuram Krishna Vijayawada (Urban) Andhra Pradesh India 520010</p>
            </div>

            <div className="sitiplay-footer-links-group">
              <span className="sitiplay-footer-links-heading">Platform</span>
              <a href="#features" className="sitiplay-footer-link">Features</a>
              <a href="#plans" className="sitiplay-footer-link">Pricing</a>
              <a href="#devices" className="sitiplay-footer-link">Devices</a>
            </div>

            <div className="sitiplay-footer-links-group">
              <span className="sitiplay-footer-links-heading">Support</span>
              <a href="#faq" className="sitiplay-footer-link">FAQ</a>
              <a href="#contact" className="sitiplay-footer-link">Contact</a>
              <a href="#" className="sitiplay-footer-link">Help Center</a>
            </div>

            <div className="sitiplay-footer-links-group">
              <span className="sitiplay-footer-links-heading">Legal</span>
              <a href="https://sitiplay.com/privacy_policy.html" target="_blank" rel="noopener noreferrer" className="sitiplay-footer-link">Privacy Policy</a>
              <a href="https://sitiplay.com/terms-and-conditions.html" target="_blank" rel="noopener noreferrer" className="sitiplay-footer-link">Terms of Service</a>
            </div>
          </div>

          {/* ── APP DOWNLOAD ────────────────────────────────────── */}

        <div className="vrplay-app-buttons">
          <a 
            href="https://play.google.com/store/apps/details?id=com.sitiplay.tv" 
            className="vrplay-store-btn vrplay-play-store-btn"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on Google Play Store"
          >
            <svg className="vrplay-store-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 010 1.732l-2.807 1.626L15.206 12l2.492-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
            </svg>
            <div className="vrplay-store-text">
              <span className="vrplay-store-label">GET IT ON</span>
              <span className="vrplay-store-name">Google Play</span>
            </div>
          </a>
        </div>

          <div className="sitiplay-footer-bottom">
            <p>© 2026 Master Channel Community Network PVT LTD (MCCN). powered by MCCN. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;